/**
 * Flow Variables Configuration
 *
 * Converts FunnelVariable records from VariableEditorContext into
 * flat menu items for the @ autocomplete in V2 text fields.
 *
 * V2-only: used by V2TemplateTextElement and MentionSuggestionMenu.
 * V1 code does not import this file.
 *
 * @module reactFlow/config/flowVariablesConfig
 */

import {
  contentSlotHelper,
  getVariableReference,
  type FunnelVariable,
} from '@modules/v2-variable-utils';

/**
 * Single item in the flow variable autocomplete menu
 */
export interface FlowVariableItem {
  /** Full reference without @, e.g. "user_goal" or "user_goal.label" */
  ref: string;
  /** Display label in menu, e.g. "@user_goal" or ".label" */
  displayLabel: string;
  /** Description shown in menu */
  description: string;
  /** Variable type for badge display */
  type: string;
  /** Whether this is a sub-accessor (indented in menu) */
  isAccessor: boolean;
  /** Parent variable ID for grouping */
  variableId: string;
}

/**
 * Accessor descriptors for enum/enum[] variables.
 * These are the dot-notation accessors beyond the base @variableId.
 */
const ENUM_ACCESSORS: ReadonlyArray<{ suffix: string; description: string }> = [
  { suffix: 'label', description: 'Option label (machine value)' },
  { suffix: 'value', description: 'Option ID (UUID)' },
];

/**
 * Convert FunnelVariable records to flat list of menu items.
 *
 * For each variable:
 * - Main item: @variableRef → display text (enum) or value (string/number)
 * - For enum/enum[] types, sub-accessors: .label, .value, plus .{slotKey} per content slot
 * - For string/number: main item only
 */
export function buildFlowVariableItems(
  variables: Record<string, FunnelVariable>,
): FlowVariableItem[] {
  const items: FlowVariableItem[] = [];

  const sortedVars = Object.values(variables).sort((a, b) =>
    getVariableReference(a).localeCompare(getVariableReference(b)),
  );

  for (const variable of sortedVars) {
    const isEnum = variable.type === 'enum' || variable.type === 'enum[]';
    const variableRef = getVariableReference(variable);

    // Main item: @variableRef
    items.push({
      ref: variableRef,
      displayLabel: `@${variableRef}`,
      description: isEnum
        ? 'Display text (what user selected)'
        : `${variable.type} value`,
      type: variable.type,
      isAccessor: false,
      variableId: variable.id,
    });

    // Sub-accessors for enum/enum[] types
    if (isEnum) {
      for (const accessor of ENUM_ACCESSORS) {
        items.push({
          ref: `${variableRef}.${accessor.suffix}`,
          displayLabel: `.${accessor.suffix}`,
          description: accessor.description,
          type: variable.type,
          isAccessor: true,
          variableId: variable.id,
        });
      }

      // Content slot accessors
      const slotKeys = contentSlotHelper.getSlotKeys(variable);
      for (const slotKey of slotKeys) {
        items.push({
          ref: `${variableRef}.${slotKey}`,
          displayLabel: `.${slotKey}`,
          description: `Content slot "${slotKey}"`,
          type: variable.type,
          isAccessor: true,
          variableId: variable.id,
        });
      }
    }
  }

  return items;
}
